'use client';

import { useTranslations } from 'next-intl';
import type { JSX } from 'react';

import { AnimatedButton } from '@/components/ui/animated-button';
import { Skeleton } from '@/components/ui/app-skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Icon } from '@/components/ui/icon';
import { Alert01Icon, Calendar03Icon, SearchRemoveIcon } from '@/components/ui/icon/glyphs';
import { Panel, PanelContent } from '@/components/ui/panel';

const LOADING_CARD_IDS: readonly string[] = [
  'first',
  'second',
  'third',
  'fourth',
  'fifth',
  'sixth',
];

export function LiveEventsFeedLoading(): JSX.Element {
  const t = useTranslations('liveEvents.feedBoundary');

  return (
    <output
      data-testid="live-events-feed-loading"
      aria-busy="true"
      className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,22rem),1fr))] gap-4"
    >
      <span className="sr-only">{t('loading')}</span>
      {LOADING_CARD_IDS.map((id) => (
        <Panel key={id} density="flush" className="h-71 overflow-hidden">
          <PanelContent className="flex h-full flex-col gap-3 p-5">
            <div className="flex gap-2" aria-hidden="true">
              <Skeleton className="h-6 w-16 rounded-pill" />
              <Skeleton className="h-6 w-28 rounded-pill" />
            </div>
            <Skeleton className="h-6 w-3/4" aria-hidden="true" />
            <Skeleton className="mt-auto h-10 w-32" aria-hidden="true" />
            <Skeleton className="h-3 w-4/5" aria-hidden="true" />
          </PanelContent>
        </Panel>
      ))}
    </output>
  );
}

export function LiveEventsFeedError({ onRetry }: { onRetry: () => void }): JSX.Element {
  const t = useTranslations('liveEvents.feedBoundary');

  return (
    <Panel data-testid="live-events-feed-error" density="compact">
      <PanelContent className="flex flex-col gap-4 py-1 sm:flex-row sm:items-center">
        <span className="grid size-10 shrink-0 place-items-center rounded-full bg-feedback-error-surface">
          <Icon icon={Alert01Icon} className="size-icon-lg text-feedback-error-content" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-heading-xs text-foreground-primary">{t('errorTitle')}</h2>
          <p className="mt-1 text-body text-foreground-secondary">{t('errorDescription')}</p>
        </div>
        <AnimatedButton variant="secondary" onClick={onRetry}>
          {t('retry')}
        </AnimatedButton>
      </PanelContent>
    </Panel>
  );
}

export function LiveEventsFeedEmpty({
  kind,
  category,
  mobile,
  onSuggest,
}: {
  kind: 'empty' | 'no-matches';
  category: string | null;
  mobile: boolean;
  onSuggest: () => void;
}): JSX.Element {
  const t = useTranslations('liveEvents.feedBoundary');
  const noMatches = kind === 'no-matches';
  const description =
    noMatches && category !== null
      ? t('noMatchesDescription', { category })
      : t('emptyDescription');

  return (
    <EmptyState
      data-testid={`live-events-feed-${kind}`}
      framed
      size="page"
      icon={noMatches ? SearchRemoveIcon : Calendar03Icon}
      title={t('emptyTitle')}
      description={description}
      action={
        mobile ? null : <AnimatedButton onClick={onSuggest}>{t('suggestEvent')}</AnimatedButton>
      }
    />
  );
}
